/**
 * Fixed configuration for the employer chat UI: capabilities and retention
 * windows. Ported from the mock's `config/`. Read by
 * components via `can(...)` and by the fixtures seam. No runtime env: the
 * employer set is fixed (the old per-role runtime switch is gone).
 */
// ── capabilities ─────────────────────────────────────────────────────────────
export interface Capabilities {
  quickReplies: boolean;
  endChat: boolean;
}

export type CapabilityKey = keyof Capabilities;

export const capabilities: Capabilities = {
  quickReplies: true,
  endChat: true,
};

export function can(capability: CapabilityKey): boolean {
  return capabilities[capability];
}

// ── retention ────────────────────────────────────────────────────────────────
/**
 * Chat retention windows. A conversation is destroyed by whichever of two
 * independent clocks fires first:
 *
 *   1. Inactivity sweep — deletes any channel idle past the backend's window.
 *      Any message from either party resets it (so the copy must say "deletes",
 *      not "reply").
 *   2. End-chat fade — `endChatWindowMs` from the *non-creator's read* of the
 *      closing message to hard delete. The creator's own read does not start it.
 *
 * Both hard-delete outright — nothing becomes an "ended, readable" chat by a
 * clock; "Ended conversations" only ever holds chats an employer ended
 * deliberately.
 *
 * `inactivityDays` is a **fallback, not the source of truth**. The backend
 * stamps the real deadline on the channel and that always wins; this only fills
 * in when no stamp has arrived, so the countdown never vanishes from the UI.
 * A blank where a deadline belongs is worse than an approximate one: it reads
 * as "this chat is not going anywhere", which is the opposite of true.
 *
 * Keep it in step with `CHANNEL_INACTIVITY_DAYS` in stream-chat-backend. It
 * being a copy is the known cost of never showing a blank.
 */
export interface RetentionConfig {
  endChatWindowMs: number;
  inactivityDays: number;
}

export const RETENTION: RetentionConfig = {
  endChatWindowMs: 30 * 60 * 1000,
  inactivityDays: 20,
};

// Gamification is deliberately absent: the coin budget arrives from the host at
// runtime (`set-gamification`), and a fixture here would advertise credits the
// host never agreed to pay.
